import { WifiOffIcon, type LucideIcon } from '@ttab/elephant-ui/icons'
import { cva } from 'class-variance-authority'
import { useEffect, useState, type PropsWithChildren, type JSX } from 'react'
import { getApplicationMenu } from '@/defaults/applicationMenuItems'
import type { YDocument } from '@/modules/yjs/hooks'
import type * as Y from 'yjs'
import { useQuery } from '@/hooks/useQuery'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { cn } from '@ttab/elephant-ui/utils'

export const Title = ({
  name,
  title,
  short: shortTitle,
  iconColor,
  icon: Icon,
  asDialog,
  ydoc,
  preview,
  titleClassName
}: {
  name: string
  title: string
  short?: string
  icon?: LucideIcon
  iconColor?: string
  asDialog?: boolean
  ydoc?: YDocument<Y.Map<unknown>>
  preview?: boolean
  titleClassName?: string
} & PropsWithChildren): JSX.Element => {
  const { connected, synced } = ydoc ?? { connected: false, synced: false }
  const [isConnected, setIsConnected] = useState(true)
  const [isSynced, setIsSynced] = useState(true)
  const { t } = useTranslation()
  const featureFlags = useFeatureFlags()

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsConnected(connected)
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [connected, synced])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsSynced(synced)
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [synced])

  const viewVariants = cva('flex grow-0 shrink basis-0 gap-2 items-center h-14 cursor-default dark:bg-secondary', {
    variants: {
      asDialog: {
        false: 'px-4'
      }
    }
  })

  const titleVariants = cva('font-bold cursor-default whitespace-nowrap opacity-90 dark:bg-secondary', {
    variants: {
      asDialog: {
        true: '',
        false: 'hidden @lg/view:block'
      }
    }
  })

  const [, setQuery] = useQuery()

  const handleEdit = () => {
    if (!preview) {
      return
    }
    setQuery({ preview: undefined })
  }

  const { icon: ViewIcon, color } = getApplicationMenu(featureFlags).groups
    .flatMap((g) => g.items)
    .find((i) => i.name === name) || {}

  const displayTitle = preview && title
    ? `${title} - ${t('planning:preview')}`
    : title
  const displayShortTitle = preview && shortTitle
    ? `${shortTitle} - ${t('planning:preview')}`
    : shortTitle

  return (
    <div className={viewVariants({ asDialog })}>

      {(!ydoc || (isConnected && isSynced)) && (
        <>
          {!Icon && !!ViewIcon && <ViewIcon size={18} strokeWidth={2.05} color={color || '#222'} onClick={handleEdit} />}
          {!!Icon && <Icon size={18} strokeWidth={2.05} color={iconColor || color || '#555'} onClick={handleEdit} />}
        </>
      )}

      {ydoc && !isConnected && (
        <>
          {isSynced
            ? <WifiOffIcon className='animate-pulse' size={18} strokeWidth={2.05} color={iconColor || color || '#555'} />
            : <WifiOffIcon className='animate-pulse fill-red-600 stroke-red-600' size={18} strokeWidth={2.05} />}
        </>
      )}

      {!!displayTitle && (
        <h2 role='header-title' className={cn(titleVariants({ asDialog: asDialog ?? false }), titleClassName)}>
          {typeof shortTitle !== 'string'
            ? <>{displayTitle}</>
            : (
                <>
                  <span className='@3xl/view:hidden'>{displayShortTitle}</span>
                  <span className='hidden @3xl/view:inline' role='header-title'>{displayTitle}</span>
                </>
              )}
        </h2>
      )}
    </div>
  )
}
